import {CartUpdateEvent, ThemeEvents} from '@theme/events';

/* global Theme */

const SOURCE = 'free-gift-reconciler';
const configElement = document.getElementById('free-gift-config');

/** @type {{enabled: boolean, giftVariantId: string | null, giftAvailable: boolean, parentProductIds: string[]} | null} */
let config = null;

try {
  const parsedConfig = JSON.parse(configElement?.textContent || 'null');
  if (parsedConfig && typeof parsedConfig === 'object') {
    config = {
      enabled: parsedConfig.enabled === true,
      giftVariantId: parsedConfig.giftVariantId
        ? String(parsedConfig.giftVariantId)
        : null,
      giftAvailable: parsedConfig.giftAvailable === true,
      parentProductIds: Array.isArray(parsedConfig.parentProductIds)
        ? parsedConfig.parentProductIds.map(String)
        : [],
    };
  }
} catch (error) {
  console.error('Could not read the free-gift configuration.', error);
}

let isReconciling = false;
let reconcileRequested = false;
let failedState = null;

function cartEndpoint(suffix) {
  const cartUrl = Theme.routes.cart_url.replace(/\/$/, '');
  return `${cartUrl}${suffix}`;
}

function ajaxEndpoint(route) {
  return route.endsWith('.js') ? route : `${route}.js`;
}

async function parseCartResponse(response) {
  const result = await response.json();

  if (!response.ok || result?.status || result?.errors) {
    throw new Error(
      result?.description ||
        result?.message ||
        result?.errors ||
        `Cart request failed (${response.status}).`,
    );
  }

  return result;
}

async function getCart() {
  const response = await fetch(cartEndpoint('.js'), {
    headers: {Accept: 'application/json'},
  });
  return parseCartResponse(response);
}

async function updateLines(updates) {
  const response = await fetch(ajaxEndpoint(Theme.routes.cart_update_url), {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({updates}),
  });
  return parseCartResponse(response);
}

async function addGift(quantity) {
  const response = await fetch(ajaxEndpoint(Theme.routes.cart_add_url), {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      items: [
        {
          id: config.giftVariantId,
          quantity,
          properties: {_free_gift: 'true'},
        },
      ],
    }),
  });
  return parseCartResponse(response);
}

function isManagedGiftLine(item) {
  return String(item?.properties?._free_gift).trim().toLowerCase() === 'true';
}

function isQualifyingParentLine(item, parentProductIds) {
  return (
    !isManagedGiftLine(item) &&
    !item?.selling_plan_allocation &&
    parentProductIds.has(String(item?.product_id))
  );
}

function getDesiredGiftQuantity(cart) {
  if (
    !config.enabled ||
    !config.giftVariantId ||
    !config.parentProductIds.length
  ) {
    return 0;
  }

  const parentProductIds = new Set(config.parentProductIds);
  return cart.items.reduce(
    (total, item) =>
      isQualifyingParentLine(item, parentProductIds)
        ? total + Math.max(0, Number(item.quantity) || 0)
        : total,
    0,
  );
}

function dispatchCartUpdate(cart) {
  document.dispatchEvent(
    new CartUpdateEvent(cart, SOURCE, {
      itemCount: cart.item_count,
      source: SOURCE,
    }),
  );
}

async function reconcileCart() {
  if (!config) return;

  let cart = await getCart();
  let didMutate = false;
  let managedGiftLines = cart.items.filter(isManagedGiftLine);
  const desiredQuantity = getDesiredGiftQuantity(cart);
  const matchingGiftLines = config.giftVariantId
    ? managedGiftLines.filter(
        (item) => String(item.variant_id) === config.giftVariantId,
      )
    : [];
  const primaryGiftLine = matchingGiftLines[0] || null;
  const currentQuantity = matchingGiftLines.reduce(
    (total, item) => total + (Number(item.quantity) || 0),
    0,
  );
  const state = `${desiredQuantity}:${currentQuantity}:${managedGiftLines
    .map((item) => item.key)
    .join(',')}`;

  if (state === failedState) return;
  failedState = null;

  const staleGiftLines = managedGiftLines.filter(
    (item) => item !== primaryGiftLine,
  );

  if (staleGiftLines.length) {
    cart = await updateLines(
      Object.fromEntries(staleGiftLines.map((item) => [item.key, 0])),
    );
    didMutate = true;
    managedGiftLines = cart.items.filter(isManagedGiftLine);
  }

  const giftLine = managedGiftLines.find(
    (item) => String(item.variant_id) === config.giftVariantId,
  );

  try {
    if (giftLine && giftLine.quantity !== desiredQuantity) {
      cart = await updateLines({[giftLine.key]: desiredQuantity});
      didMutate = true;
    } else if (!giftLine && desiredQuantity > 0 && config.giftAvailable) {
      await addGift(desiredQuantity);
      cart = await getCart();
      didMutate = true;
    }
  } catch (error) {
    // Gift inventory must never prevent the paid item from remaining in cart.
    failedState = state;
    console.warn('Free gift is currently unavailable; paid cart items were preserved.', error);
  }

  if (didMutate) dispatchCartUpdate(cart);
}

async function requestReconciliation() {
  reconcileRequested = true;
  if (isReconciling) return;

  isReconciling = true;
  try {
    while (reconcileRequested) {
      reconcileRequested = false;
      await reconcileCart();
    }
  } catch (error) {
    console.error('Free-gift cart reconciliation failed.', error);
  } finally {
    isReconciling = false;
  }
}

if (config) {
  document.addEventListener(ThemeEvents.cartUpdate, (event) => {
    if (event.detail?.data?.source === SOURCE) return;
    requestReconciliation();
  });

  requestReconciliation();
}
